import { axios } from '../configs'

const WEB_PUSH_SW_URL = '/web-push-sw.js'
const WEB_PUSH_SW_SCOPE = '/web-push/'
const SUBSCRIBE_ENDPOINT = 'push/subscribe'
const PENDING_UNSUBSCRIBE_STORAGE_KEY = 'web-push:pending-unsubscribe:v1'

function readPendingUnsubscribeEndpoint(): string | null {
  try {
    return localStorage.getItem(PENDING_UNSUBSCRIBE_STORAGE_KEY)
  } catch {
    return null
  }
}

function recordPendingUnsubscribe(endpoint: string): void {
  try {
    localStorage.setItem(PENDING_UNSUBSCRIBE_STORAGE_KEY, endpoint)
  } catch {
    // ストレージ unavailable: 再試行を記録できないだけなので続行
  }
}

function clearPendingUnsubscribe(): void {
  try {
    localStorage.removeItem(PENDING_UNSUBSCRIBE_STORAGE_KEY)
  } catch {
    // noop
  }
}

// 再購読と保留解除の再試行が同じ購読を取り合わないよう、このタブ内で直列に実行する
let subscriptionTaskQueue: Promise<unknown> = Promise.resolve()

function runSubscriptionTask<T>(task: () => Promise<T>): Promise<T> {
  const result = subscriptionTaskQueue.then(task)
  subscriptionTaskQueue = result.catch(() => undefined)
  return result
}

async function getWebPushRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }
  const registration = await navigator.serviceWorker.getRegistration(
    WEB_PUSH_SW_SCOPE,
  )
  if (!registration) return null

  // getRegistration は最長前方一致。旧ルートスコープの登録を扱わない。
  const scopePath = new URL(registration.scope).pathname
  if (scopePath !== WEB_PUSH_SW_SCOPE) return null
  return registration
}

// CRA がクライアントバンドルへ公開する env は REACT_APP_* のみ。backend は同じ鍵を
// VAPID_PUBLIC_KEY として読むため、両者の値は常に一致させること。
function getVapidPublicKey(): string {
  return process.env.REACT_APP_VAPID_PUBLIC_KEY ?? ''
}

type PushSubscribeBody = {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
  userAgent?: string
}

/**
 * URL-safe base64 の VAPID 公開鍵を、PushManager.subscribe が applicationServerKey
 * として要求する Uint8Array へデコードする。ライブラリ不要。
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/** Web Push API が揃うブラウザを対象にする。 */
export function isWebPushEligible(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const supportsServiceWorker = 'serviceWorker' in navigator
  const supportsPush = 'PushManager' in window
  const supportsNotification = 'Notification' in window
  return supportsServiceWorker && supportsPush && supportsNotification
}

async function registerWebPushServiceWorker(): Promise<ServiceWorkerRegistration> {
  // register() は冪等。getRegistration() は旧ルートスコープを返し得る。
  const registration = await navigator.serviceWorker.register(WEB_PUSH_SW_URL, {
    scope: WEB_PUSH_SW_SCOPE,
  })
  await registration.update().catch(() => {
    // 更新チェックの失敗は無視する（登録自体は成立している）。
  })
  return registration
}

// 既存購読が現行の VAPID 公開鍵で作られたものか判定する。鍵が取得できない購読を
// 「一致」とみなすと、ローテーション後に送信不能(403→サーバ側で行削除)の購読を
// 起動ごとに再登録するループから永遠に抜け出せないため、確認できないときは
// 作り直す側に倒す（最悪でも余分な再購読で済み、通知は機能し続ける）。
function hasCurrentApplicationServerKey(
  subscription: PushSubscription,
  expectedKey: Uint8Array,
): boolean {
  const currentKey = subscription.options?.applicationServerKey
  if (!currentKey) return false
  const currentBytes = new Uint8Array(currentKey)
  return (
    currentBytes.length === expectedKey.length &&
    currentBytes.every((byte, index) => byte === expectedKey[index])
  )
}

async function getOrCreateSubscription(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription> {
  const applicationServerKey = urlBase64ToUint8Array(getVapidPublicKey())
  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    if (hasCurrentApplicationServerKey(existing, applicationServerKey)) {
      return existing
    }
    // VAPID 鍵ローテーション後の旧鍵購読は送信不能(403→サーバ側で削除)のまま
    // 起動ごとに再登録されループするため、現行鍵で購読を作り直す。
    await existing.unsubscribe().catch(() => {
      // 解除に失敗しても subscribe 側で置き換えを試みる。
    })
  }
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  })
}

function toSubscribeBody(
  subscription: PushSubscription,
): PushSubscribeBody | null {
  const json = subscription.toJSON()
  const endpoint = json.endpoint
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!endpoint || !p256dh || !auth) return null
  return {
    endpoint,
    keys: { p256dh, auth },
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  }
}

/**
 * ログイン中の運営を Web Push 通知に購読させる。
 *
 * iOS では Notification.requestPermission と PushManager.subscribe の両方が
 * ユーザー操作(user activation)を要求するため、必ずユーザージェスチャーから呼ぶこと。
 * 非対応ブラウザや VAPID 鍵未設定時は何もしない。現行の VAPID 鍵で作られた
 * 既存のブラウザ購読は再利用し、二重購読は作らない（鍵が異なる・確認できない購読は
 * 作り直す）。
 *
 * @returns backend への購読登録が完了した場合 true
 */
export async function subscribeToWebPush(): Promise<boolean> {
  if (!isWebPushEligible() || !getVapidPublicKey()) return false

  // 許可済み(granted)なら確認プロンプトを出さない（再インストール後やサーバ側削除後の
  // サイレント再購読など）。未決定のときだけ許可を求める。
  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return false
  }

  return runSubscriptionTask(registerBrowserSubscription)
}

async function registerBrowserSubscription(): Promise<boolean> {
  const registration = await registerWebPushServiceWorker()
  const subscription = await getOrCreateSubscription(registration)
  const body = toSubscribeBody(subscription)
  if (!body) return false

  await axios.post(SUBSCRIBE_ENDPOINT, body)
  // 意図的な再購読が成立したので、保留中の解除再試行は取り消す
  clearPendingUnsubscribe()
  return true
}

/**
 * このブラウザの Web Push 購読を解除し、backend にも削除を依頼する。
 * どのプラットフォームでもログアウト時に安全に呼べる。未購読なら何もしない。
 * ブラウザ側の解除に失敗した場合は pending 記録し、次回起動時に再試行する。
 * @param expectedAuthUid 指定すると、その利用者のトークンでだけ backend へ削除を依頼する
 */
export async function unsubscribeFromWebPush(
  expectedAuthUid?: string,
): Promise<void> {
  // 401 応答処理（強制ログアウト）から呼ばれ得るため、POST を待つ直列化には入れない（デッドロック回避）
  const registration = await getWebPushRegistration()
  if (!registration) return

  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  const endpoint = subscription.endpoint
  let browserUnsubscribed = true
  await subscription.unsubscribe().catch(() => {
    browserUnsubscribed = false
  })
  if (!browserUnsubscribed) {
    recordPendingUnsubscribe(endpoint)
  }

  await axios.delete(SUBSCRIBE_ENDPOINT, {
    data: { endpoint },
    ...(expectedAuthUid ? { expectedAuthUid } : {}),
  })
}

/**
 * 前回ログアウト時にブラウザ側解除へ失敗した購読の解除を再試行する。
 * 起動時に best-effort で呼ぶ。再購読が成立していた場合（pending が消えている、
 * または別 endpoint の購読がある）は何もしない。
 */
export function retryPendingPushUnsubscribe(): Promise<void> {
  return runSubscriptionTask(unsubscribePendingEndpoint)
}

async function unsubscribePendingEndpoint(): Promise<void> {
  const endpoint = readPendingUnsubscribeEndpoint()
  if (!endpoint) return

  try {
    const registration = await getWebPushRegistration()
    if (!registration) {
      clearPendingUnsubscribe()
      return
    }
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription || subscription.endpoint !== endpoint) {
      clearPendingUnsubscribe()
      return
    }
    // 別タブで再購読（pending クリア）が起きていたら解除しない
    if (readPendingUnsubscribeEndpoint() !== endpoint) return
    const unsubscribed = await subscription
      .unsubscribe()
      .catch(() => false)
    if (unsubscribed) {
      clearPendingUnsubscribe()
    }
  } catch {
    // 失敗時は pending を残し、次の起動で再試行する
  }
}
